"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AppPlaceholder,
  useSitecore,
  type LinkField,
  type Page,
  ComponentRendering,
  ComponentFields,
  Text,
} from "@sitecore-content-sdk/nextjs";
import React, {
  type JSX,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Icon from "../../atoms/Icon";
import Button from "../../atoms/Button";
import useBreakpoint from "../../hooks/useBreakpoint";
import { ICONS } from "../../variables/icons";
import { NoDataFallback } from "../../utils/NoDataFallback";
import {
  MegaMenuLevel1,
  MegaMenuLevel2,
  MegaMenuLevel3,
  MegaMenuProps,
} from "components/mega-menu/mega-menu.props";
import { encodeHashValue } from "lib/utils";
import styles from "./MegaMenu.module.scss";
import { isExternalURL } from "src/utils/contentHubComponentsUtils";

type ComponentMap = ReturnType<typeof useSitecore>["componentMap"];

type SearchIconComponentProps = {
  searchPlaceholder: string | undefined;
  rendering: MegaMenuProps["rendering"];
  page: Page;
  componentMap: ComponentMap;
};

const SearchIconComponent = ({
  searchPlaceholder,
  rendering,
  page,
  componentMap,
}: SearchIconComponentProps) => {
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      if (buttonRef?.current?.contains(targetNode)) {
        return;
      }
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(targetNode)
      ) {
        setShowSearch(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (showSearch && searchWrapperRef.current) {
      const inputElement = searchWrapperRef.current.querySelector(
        'input, [contenteditable="true"]',
      );
      if (inputElement instanceof HTMLElement) {
        inputElement.focus();
      }
    }
  }, [showSearch]);

  return (
    <React.Fragment>
      <button
        ref={buttonRef}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.currentTarget.blur();
          setShowSearch((prev) => !prev);
        }}
        aria-label={showSearch ? "Clear" : "Search"}
        className={`flex items-center justify-center w-[55px] h-[55px] relative z-[1000] xl:bottom-[10px] xl:w-[36px] xl:h-[36px] bg-ecolab-blue rounded-full text-white shadow-[0_8px_16px_rgba(0,0,0,0.15)] hover:bg-dark-blue active:bg-dark-blue cursor-pointer ${
          showSearch ? "!bg-dark-blue" : ""
        }`}
      >
        <span className="w-[18px] h-[18px] hover:bg-dark-blue relative">
          <Icon
            icon={ICONS.CLOSE}
            className={`w-[18px] h-[18px] !absolute transition-opacity duration-300 ease-in-out ${
              showSearch ? "opacity-[1]" : "opacity-[0]"
            }`}
          />
          <Icon
            icon={ICONS.SEARCH}
            className={`w-[18px] h-[18px] !absolute transition-opacity duration-300 ease-in-out ${
              showSearch ? "opacity-[0]" : "opacity-[1]"
            }`}
          />
        </span>
      </button>

      {/* Conditional render prevents loading 7.4MB Coveo bundle until user clicks */}
      {showSearch && (
        <div ref={searchWrapperRef} style={{ zIndex: 9999 }}>
          <AppPlaceholder
            name={searchPlaceholder || "searchicon-7"}
            rendering={rendering}
            page={page}
            componentMap={componentMap}
          />
        </div>
      )}
    </React.Fragment>
  );
};

const LogoComponent = React.memo(
  ({
    className,
    logoOpacity,
    selectedPlaceholder,
    rendering,
    page,
    componentMap,
  }: {
    className: string;
    logoOpacity: string;
    selectedPlaceholder: string | undefined;
    rendering: ComponentRendering<ComponentFields>;
    page: Page;
    componentMap: ComponentMap;
  }) => (
    <div className={`${className} ${logoOpacity}`}>
      <AppPlaceholder
        key={selectedPlaceholder}
        name={selectedPlaceholder || "mainnavlogo-7"}
        rendering={rendering}
        page={page}
        componentMap={componentMap}
      />
    </div>
  ),
);

LogoComponent.displayName = "LogoComponent";

export const buildHashUrlForMegaMenu = (
  industry: string,
  market: string,
  application: string,
  solution: string,
): string => {
  const hashParts: string[] = [];
  if (industry) hashParts.push(`f-taxonomy_industries=${industry}`);
  if (market) hashParts.push(`f-taxonomy_markets=${market}`);
  if (application) hashParts.push(`f-taxonomy_applications=${application}`);
  if (solution) hashParts.push(`f-taxonomy_solutions=${solution}`);
  return hashParts.length > 0 ? `#${hashParts.join("&")}` : "";
};

export const Default = (props: MegaMenuProps): JSX.Element => {
  console.log("---->[MegaMenu] Component", props.fields);

  const { page, fields, rendering } = props;
  // componentMap from SitecoreProvider context — see Navigation.tsx note;
  // a static `import componentMap from '.sitecore/component-map'` would
  // create a load-time TDZ cycle on the `Default` export.
  const { componentMap } = useSitecore();

  const [openLevel1Index, setOpenLevel1Index] = useState<number | null>(null);
  const [openLevel2IndexMap, setOpenLevel2IndexMap] = useState<
    Record<number, number | null>
  >({});
  const [openLevel3IndexMap, setOpenLevel3IndexMap] = useState<
    Record<string, number | null>
  >({});
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const tabletRefs = useRef<(HTMLDivElement | null)[]>([]);
  const previousViewportWidthRef = useRef<number | null>(null);
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  const placeholderKeys = Object.keys(rendering?.placeholders || {});
  const headerPlaceholder = placeholderKeys.find(
    (key) =>
      key.startsWith("mainnavlogo") && !key.toLowerCase().includes("nalco"),
  );
  const nalcoPlaceholder = placeholderKeys.find((key) =>
    key.toLowerCase().includes("mainnavnalcologo"),
  );
  const searchPlaceholder = placeholderKeys.find((key) =>
    key.startsWith("searchicon"),
  );
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [level1ClickedOutside, setLevel1ClickedOutside] = useState(false);
  const [logoOpacity, setLogoOpacity] = useState("opacity-0");
  const [selectedPlaceholder, setSelectedPlaceholder] = useState<
    string | undefined
  >(undefined);
  const level1Items = fields?.c || [];

  // Reset all menus
  const resetMenuState = useCallback(() => {
    setOpenLevel1Index(null);
    setOpenLevel2IndexMap({});
    setOpenLevel3IndexMap({});
    setShowMobileMenu(false);
  }, []);

  // Get open level 2 index based on currently open level 1 index
  const getOpenLevel2Index = useCallback(
    () =>
      openLevel1Index === null
        ? null
        : (openLevel2IndexMap[openLevel1Index] ?? null),
    [openLevel1Index, openLevel2IndexMap],
  );

  // Get open level 3 index based on currently open level 1 and level 2 indices
  const getOpenLevel3Index = useCallback(() => {
    const level2Index = getOpenLevel2Index();
    if (openLevel1Index !== null && level2Index !== null) {
      return openLevel3IndexMap[`${openLevel1Index}-${level2Index}`] ?? null;
    }
    return null;
  }, [
    openLevel1Index,
    openLevel2IndexMap,
    openLevel3IndexMap,
    getOpenLevel2Index,
  ]);

  //helpers for handlclick fucntion
  const hasChildren = (item?: any) => !!item?.c?.length;

  const getHref = (item?: any) => item?.l?.h;

  const navigateIfLeaf = (item?: any) => {
    const href = getHref(item);
    if (!hasChildren(item) && href) {
      globalThis.location.href = href;
      return true;
    }
    return false;
  };

  const handleMobileLevel1 = (index: number) => {
    setOpenLevel1Index(index === openLevel1Index ? null : index);
    setActiveMenu(index);
    setLevel1ClickedOutside(false);
    setOpenLevel2IndexMap({});
    setOpenLevel3IndexMap({});
  };

  const handleMobileLevel2 = (index: number, item?: any) => {
    if (navigateIfLeaf(item)) return;

    if (openLevel1Index !== null) {
      setOpenLevel2IndexMap((prev) => ({
        ...prev,
        [openLevel1Index]: index === prev[openLevel1Index] ? null : index,
      }));
      setOpenLevel3IndexMap({});
    }
  };

  const handleMobileLevel3 = (index: number, item?: any) => {
    if (navigateIfLeaf(item)) return;

    if (openLevel1Index !== null && openLevel2Index !== null) {
      const key = `${openLevel1Index}-${openLevel2Index}`;

      setOpenLevel3IndexMap((prev) => ({
        ...prev,
        [key]: index === prev[key] ? null : index,
      }));
    }
  };

  const handleDesktopLevel1 = (index: number) => {
    setOpenLevel1Index(index === openLevel1Index ? null : index);
  };

  const handleDesktopLevel2 = (index: number, item?: any) => {
    if (navigateIfLeaf(item)) return;

    if (openLevel1Index !== null) {
      setOpenLevel2IndexMap((prev) => ({
        ...prev,
        [openLevel1Index]: index,
      }));
    }
  };

  // Direct calls — the manual useMemo wrappers were skipped by React Compiler
  // (function deps recreate each render, defeating memoization). Let the
  // compiler memoize instead.
  const openLevel2Index = getOpenLevel2Index();
  const openLevel3Index = getOpenLevel3Index();

  const handleDesktopLevel3 = (index: number, item?: any) => {
    if (navigateIfLeaf(item)) return;

    const level2Index =
      openLevel1Index === null
        ? undefined
        : openLevel2IndexMap[openLevel1Index];

    if (openLevel1Index !== null && level2Index !== undefined) {
      const key = `${openLevel1Index}-${level2Index}`;

      setOpenLevel3IndexMap((prev) => ({
        ...prev,
        [key]: index,
      }));
    }
  };

  //handlclick fucntion for mobile,tablet and desktop to open and close menu based on level and index
  const handleClick = useCallback(
    (
      level: number,
      index: number,
      item?: MegaMenuLevel1 | MegaMenuLevel2 | MegaMenuLevel3,
    ) => {
      if (isMobile || isTablet) {
        if (level === 1) return hasChildren(item) && handleMobileLevel1(index);
        if (level === 2) return handleMobileLevel2(index, item);
        if (level === 3) return handleMobileLevel3(index, item);
        return;
      }

      // Desktop
      if (level === 1) return handleDesktopLevel1(index);
      if (level === 2) return handleDesktopLevel2(index, item);
      if (level === 3) return handleDesktopLevel3(index, item);
    },
    [isMobile, isTablet, openLevel1Index, openLevel2IndexMap, openLevel2Index],
  );

  //Utility variables
  const level2Items =
    openLevel1Index === null ? [] : (level1Items?.[openLevel1Index]?.c ?? []);
  const level3Items =
    openLevel2Index === null ? [] : (level2Items?.[openLevel2Index]?.c ?? []);
  const level4Items =
    openLevel3Index === null ? [] : (level3Items?.[openLevel3Index]?.c ?? []);

  const hasLevel2Children =
    openLevel2Index !== null &&
    openLevel2Index >= 0 &&
    level2Items.length > openLevel2Index &&
    !!level2Items[openLevel2Index]?.c?.length;

  const hasLevel3Children =
    openLevel3Index !== null &&
    level3Items.length > openLevel3Index &&
    !!level3Items[openLevel3Index]?.c?.length;

  const hasDeepNestedChildren =
    openLevel1Index !== null &&
    level1Items?.[openLevel1Index]?.c?.some((level2: any) =>
      level2?.c?.some(
        (level3: any) => Array.isArray(level3?.c) && level3.c.length > 0,
      ),
    );

  const listUlStyle = showMobileMenu
    ? `${styles.level1Animation}  max-h-[2000px] overflow-hidden visible`
    : `${styles.level1Animation}  max-h-[0] overflow-hidden invisible`;

  //hamburgerIcon for mobile and tablet screen with animation
  const hamburgerIcon = () => {
    const durationStyle = "duration-300 ease-in-out rounded-[1px]";
    const hambStyle = "block h-1 bg-white w-full";
    return (
      <span className="relative w-[25px] h-6 flex flex-col justify-between items-center group z-1">
        <span
          className={`${hambStyle} transition-transform ${durationStyle} ${
            showMobileMenu
              ? "transform rotate-45 translate-y-[10px] mt-0"
              : "mt-1"
          }`}
        ></span>
        <span
          className={`${hambStyle} transition-opacity ${durationStyle} ${
            showMobileMenu ? "opacity-0 mt-0" : "opacity-100 mt-1"
          }`}
        ></span>
        <span
          className={`${hambStyle} transition-transform ${durationStyle} ${
            showMobileMenu
              ? "transform -rotate-45 -translate-y-[10px] mt-0"
              : "mt-1"
          }`}
        ></span>
      </span>
    );
  };

  // Handler for mobile menu toggle, resets menu state when closing
  const handleMobileMenuToggle = () => {
    setActiveMenu(null);
    setShowMobileMenu((prev) => {
      const newState = !prev;
      if (!newState) {
        resetMenuState();
      }
      return newState;
    });
  };

  //Helpers for renderlink function
  const hasHref = (item: any) => !!item?.l?.h;
  const getLinkField = (item: any) => item?.l;
  const getLayoutClasses = (
    level: number,
    variant: string,
    isActive: boolean,
    level1ClickedOutside: boolean,
  ) => {
    const isTopLevel = level === 1;

    if (variant === "desktop") {
      return "flex-row my-3 mx-4";
    }

    if (isTopLevel && isActive) {
      return `flex-row py-[20px] px-[24px] font-normal ${
        level1ClickedOutside ? "!bg-dark-blue !no-underline" : ""
      }`;
    }

    return "flex-row-reverse mb-3 py-[20px] px-[24px]";
  };
  const getVariantClass = (variant: string) => {
    if (variant === "mobile")
      return "-mx-[20px] -mt-[30px] hover:underline focus:underline active:underline outline-none";

    if (variant === "tablet") return "mx-0 mt-0";

    return "";
  };
  const getActiveClass = (
    level: number,
    variant: string,
    isActive: boolean,
    isLastSelected: boolean,
  ) => {
    if (level === 1 && isActive) {
      return `text-white flex-row bg-ecolab-blue underline ${getVariantClass(variant)}`;
    }

    return `${styles.level1Animation} ${
      isLastSelected ? "text-ecolab-blue" : ""
    } bg-white text-black flex-row-reverse hover:text-ecolab-blue active:text-ecolab-blue`;
  };

  const getIconClass = (
    level: number,
    variant: string,
    isActive: boolean,
    isLastSelected: boolean,
  ) => {
    const isTopLevel = level === 1;
    const isMobileOrTablet =
      (isTopLevel && variant === "mobile") || variant === "tablet";

    if (isMobileOrTablet && isActive) return "text-white rotate-180 pl-3";

    if (isMobileOrTablet && !isActive)
      return `${isLastSelected ? "text-ecolab-blue" : ""} text-black w-[6px] h-[10px]`;

    return "text-black w-[6px] h-[10px]";
  };

  // Separate render function for desktop level 1 to apply different styles and structure
  const renderDesktopLevel1 = (item: any, isActive: boolean) => {
    const link = getLinkField(item);

    if (hasHref(item)) {
      return (
        <div className="rounded cursor-pointer transition-colors duration-200">
          <div className="flex flex-row">
            <Button
              className={`!pl-4 !py-4 !pr-1 !mr-0 line-clamp-2 ${
                isActive
                  ? "text-black font-medium"
                  : "text-gray hover:text-ecolab-blue focus:text-charcoal-gray font-normal"
              }`}
              field={link}
              tabIndex={0}
              aria-label={link?.t}
              data-role="link"
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-row pl-4 py-4 items-start">
        <Text
          tag="span"
          field={{ value: item.t }}
          className={`!py-0 !pl-0 pr-1 focus:font-medium line-clamp-2 cursor-pointer ${
            isActive
              ? "text-black font-medium"
              : "text-gray hover:text-ecolab-blue focus:text-charcoal-gray font-normal"
          }`}
        />
        <Icon
          icon={ICONS.CARET}
          className={` min-h-7  ${
            isActive ? "!pt-0  rotate-180 pr-1 pl-[10px]" : "pl-1 pr-[10px]"
          }`}
        />
      </div>
    );
  };

  //renderLink function to render links for all levels with different styles and icons based on level, variant and active state
  const renderLink = (
    item: any,
    isActive: boolean,
    level: number,
    variant: "mobile" | "tablet" | "desktop" = "mobile",
    isLastSelected = false,
    options?: {
      ref?: (el: HTMLDivElement | null) => void;
      onClick?: React.MouseEventHandler<HTMLDivElement>;
      onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
      role?: string;
      tabIndex?: number;
      ariaHaspopup?: boolean;
      ariaExpanded?: boolean;
      ariaControls?: string;
    },
  ) => {
    const link = getLinkField(item);

    // Desktop Level 1 Special Case
    if (variant === "desktop" && level === 1) {
      return renderDesktopLevel1(item, isActive);
    }

    const baseClass =
      "flex items-center font-medium leading-5 tracking-[0.4px] border-t-2 shadow-btn border-t-ecolab-blue gap-2 cursor-pointer";

    const justifyClass =
      level === 1 && isActive && (variant === "mobile" || variant === "tablet")
        ? ""
        : "justify-between";

    const layoutClass = getLayoutClasses(
      level,
      variant,
      isActive,
      level1ClickedOutside,
    );

    const activeClass = getActiveClass(
      level,
      variant,
      isActive,
      isLastSelected,
    );

    const iconClass = getIconClass(level, variant, isActive, isLastSelected);

    const combinedClass = `group ${baseClass} ${layoutClass} ${activeClass} ${justifyClass}`;

    if (hasHref(item)) {
      const mappedLink: LinkField = {
        value: {
          href: item?.l?.h,
          text: item?.l?.t || item?.t,
          linktype: item?.l?.h?.startsWith("http") ? "external" : "internal",
        },
      };
      return (
        <div
          className={`${combinedClass} !justify-end`}
          tabIndex={0}
          role="link"
          aria-label={link?.t}
        >
          <Button
            className={`${
              isActive
                ? ""
                : "group-hover:text-ecolab-blue group-active:text-ecolab-blue"
            } ${
              isDesktop ? "px-6 py-3 !min-h-[72px]" : "!px-0 !py-0 !min-h-auto"
            }  !mr-0 !mb-0 !min-w-auto !font-medium !w-full justify-between! line-clamp-2 !rounded-none !justify-end !flex-nowrap [&_span]:whitespace-normal`}
            field={mappedLink}
            tabIndex={-1}
            iconPosition="right"
            jssIconField={{ value: ICONS.CHEVRON_BLUE_RIGHT }}
            aria-label={link?.t}
            data-role="link"
          />
        </div>
      );
    }

    return (
      <div
        ref={options?.ref}
        className={`${combinedClass} focus-visible:outline-offset-[1px] focus-visible:text-ecolab-blue px-6 py-3 ${
          isDesktop ? "min-h-[72px]" : ""
        }`}
        onClick={options?.onClick}
        onKeyDown={options?.onKeyDown}
        role={options?.role}
        tabIndex={options?.tabIndex}
        aria-haspopup={options?.ariaHaspopup}
        aria-expanded={options?.ariaExpanded}
        aria-controls={options?.ariaControls}
      >
        <Icon
          icon={ICONS.CHEVRON_BLUE_RIGHT}
          className={`${iconClass} ${
            isActive
              ? ""
              : "group-hover:text-ecolab-blue group-active:text-ecolab-blue"
          }`}
        />
        <Text tag="span" field={{ value: item.t }} className="line-clamp-2" />
      </div>
    );
  };

  //Extracted Level 4 Links rendering to reduce nesting in main render function and improve readability
  const renderLevel4LinksForMobileAndTablet = (
    links: any[],
    selectedIndustry?: string,
    selectedMarket?: string,
    selectedApplication?: string,
  ) => {
    return links.map((link: any, linkIndex: number) => {
      let href = link?.l?.h;
      if (href && (selectedIndustry || selectedMarket)) {
        const url = new URL(href, globalThis.location.origin);
        let industry = "";
        let markets = "";
        let application = "";
        let solution = link?.l?.s ?? "";
        if (selectedIndustry) {
          industry = encodeHashValue(selectedIndustry);
        }
        if (selectedMarket) {
          markets = encodeHashValue(selectedMarket);
        }
        if (selectedApplication) {
          application = encodeHashValue(selectedApplication);
        }
        if (solution) {
          solution = encodeHashValue(solution);
        }
        const hashPart = buildHashUrlForMegaMenu(
          industry,
          markets,
          application,
          solution,
        );
        url.hash = hashPart.startsWith("#") ? hashPart.substring(1) : hashPart;
        href = url.toString();
      }

      const mappedLink: LinkField = {
        value: {
          href: href,
          text: link?.l?.t || link?.t,
          linktype:
            link?.l?.h?.startsWith("http") && isExternalURL(link?.l?.h)
              ? "external"
              : "internal",
        },
      };

      return (
        <li
          key={`l4-link-${linkIndex}-${link?.l?.t || linkIndex}`}
          onClick={resetMenuState}
        >
          <Button
            buttonType={link?.l?.class || ""}
            className="text-ecolab-blue font-normal flex items-center gap-2 !py-[6px] hover:underline active:underline !px-0 !mb-0 !min-h-0 !min-w-0 !mr-0 !justify-end !flex-nowrap"
            field={mappedLink}
            iconPosition="right"
            jssIconField={{ value: ICONS.CHEVRON_BLUE_RIGHT }}
          />
        </li>
      );
    });
  };

  //function to render nested levels 4 for mobile and tablet
  const renderLevel4 = (level2Items: any[]) => {
    if (openLevel2Index === null || openLevel3Index === null) {
      return null;
    }
    const level4Data =
      level2Items[openLevel2Index]?.c?.[openLevel3Index]?.c || [];
    const selectedIndustry =
      openLevel2Index === null ? undefined : level2Items[openLevel2Index]?.i;
    const selectedMarket =
      openLevel3Index === null ? undefined : level3Items[openLevel3Index]?.m;

    if (level4Data.length === 0) return null;

    return (
      <>
        <Text
          tag="h3"
          field={{ value: fields?.b3 }}
          className="font-medium pt-5 mb-5 mt-0 text-black text-2xl"
        />

        <ul className="bg-white -mx-[20px] pl-[20px]">
          {level4Data.map((level4Item: any, i4: number) => (
            <li
              key={`l4-${i4}-${level4Item.t || i4}`}
              className="pr-5 pb-5 pt-[10px]"
              data-testid={`level4-item-${i4}`}
            >
              <Text
                tag="p"
                field={{ value: level4Item?.t }}
                className="font-medium mb-[10px] text-black"
              />

              <ul>
                {renderLevel4LinksForMobileAndTablet(
                  level4Item.c || [],
                  selectedIndustry,
                  selectedMarket,
                  level4Item?.t,
                )}
              </ul>
            </li>
          ))}
        </ul>
      </>
    );
  };

  //function to render nested levels 2 and 3 for mobile and tablet
  const renderNestedLevels = (level2Items: any[]) => {
    return (
      <>
        {/* LEVEL 2 */}
        {openLevel2Index === null && (
          <>
            {hasDeepNestedChildren && (
              <Text
                tag="h3"
                field={{ value: fields?.b1 }}
                className="font-medium pt-5 mb-5 mt-0 text-black text-2xl"
              />
            )}

            <ul className={`${hasDeepNestedChildren ? "" : "pt-[20px]"}`}>
              {level2Items.map((level2Item: any, i2: number) => (
                <li
                  key={`l2-${i2}-${level2Item.t || i2}`}
                  data-testid={`level2-item-${i2}`}
                >
                  <span onClick={() => handleClick(2, i2, level2Item)}>
                    {renderLink(level2Item, openLevel2Index === i2, 2)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* LEVEL 3 */}
        {openLevel2Index !== null && openLevel3Index === null && (
          <>
            {hasDeepNestedChildren && (
              <Text
                tag="h3"
                field={{ value: fields?.b2 }}
                className="font-medium pt-5 mb-5 mt-0 text-black text-2xl"
              />
            )}

            <ul>
              {(level2Items[openLevel2Index]?.c || []).map(
                (level3Item: any, i3: number) => (
                  <li
                    key={`l3-${i3}-${level3Item.t || i3}`}
                    data-testid={`level3-item-${i3}`}
                  >
                    <span onClick={() => handleClick(3, i3, level3Item)}>
                      {renderLink(level3Item, openLevel3Index === i3, 3)}
                    </span>
                  </li>
                ),
              )}
            </ul>
          </>
        )}

        {/* LEVEL 4 */}
        {openLevel2Index !== null &&
          openLevel3Index !== null &&
          renderLevel4(level2Items)}
      </>
    );
  };

  //function to render the responsive menu for mobile and tablet with logo, search icon and hamburger menu
  const renderResponsiveMenu = () => {
    return (
      <div className="megamenu-component" data-role="megamenu-component">
        <div
          data-testid="mega-menu"
          className={`flex justify-between items-center h-[95px] max-w-(--sitewide-wide-container-max-width) mx-auto ${
            showMobileMenu ? "" : "shadow-[0_4px_6px_-4px_rgba(0,0,0,0.3)]"
          }`}
        >
          <LogoComponent
            className="min-h-[82px] pl-[20px] lg:pl-[44px] pt-[20px] lg:pt-[10px] pb-0 [&_img]:rounded-none [&>div]:mb-0 [&_img]:max-h-[42px] [&_img]:object-contain [&_img]:max-w-[146px]"
            selectedPlaceholder={selectedPlaceholder}
            logoOpacity={logoOpacity}
            rendering={rendering}
            page={page}
            componentMap={componentMap}
          />

          <div className="flex items-center gap-5">
            <div className="px-2 pt-[10px] pb-3">
              <SearchIconComponent
                searchPlaceholder={searchPlaceholder}
                rendering={rendering}
                page={page}
                componentMap={componentMap}
              />
            </div>

            <button
              data-testid="menu-toggle"
              onClick={handleMobileMenuToggle}
              className={`relative flex items-center justify-center text-white cursor-pointer min-w-[75px] min-h-[95px] ${
                styles.hamburgerIcon
              } ${showMobileMenu ? styles.closeIcon : ""}`}
              aria-label={showMobileMenu ? "Close Icon" : "Menu Icon"}
            >
              {hamburgerIcon()}
            </button>
          </div>
        </div>

        {/* level 1 */}
        <div
          className={`max-w-(--sitewide-wide-container-max-width) mx-auto
        ${listUlStyle}
        absolute z-[20] w-full
        lg:static lg:z-auto lg:w-auto`}
        >
          <ul className="pt-[30px] px-[20px] pb-[18px] bg-light-gray">
            {/* MOBILE */}
            <div className="lg:hidden">
              {openLevel1Index === null
                ? level1Items.map((level1Item, i1) => (
                    <li
                      key={`l1-${i1}-${level1Item.t || i1}`}
                      data-testid={`level1-item-mobile-${i1}`}
                    >
                      <span
                        ref={mobileRef}
                        onClick={() => handleClick(1, i1, level1Item)}
                      >
                        {renderLink(
                          level1Item,
                          false,
                          1,
                          undefined,
                          activeMenu === i1,
                        )}
                      </span>
                    </li>
                  ))
                : (() => {
                    const level1Item = level1Items[openLevel1Index];
                    const level2Items = level1Item.c || [];

                    return (
                      <li>
                        <span
                          onClick={() =>
                            handleClick(1, openLevel1Index, level1Item)
                          }
                        >
                          {renderLink(level1Item, true, 1)}
                        </span>

                        {renderNestedLevels(level2Items)}
                      </li>
                    );
                  })()}
            </div>

            {/* TABLET */}
            <div className="hidden lg:block xl:hidden">
              {level1Items.map((level1Item, i1) => {
                const isActive = openLevel1Index === i1;
                const level2Items = level1Item.c || [];

                return (
                  <li
                    key={`t-l1-${i1}-${level1Item.t || i1}`}
                    data-testid={`level1-item-tablet-${i1}`}
                  >
                    <div
                      ref={(el) => {
                        tabletRefs.current[i1] = el;
                      }}
                      onClick={() => handleClick(1, i1, level1Item)}
                    >
                      {renderLink(
                        level1Item,
                        isActive,
                        1,
                        "tablet",
                        activeMenu === i1,
                      )}
                    </div>

                    {isActive && renderNestedLevels(level2Items)}
                  </li>
                );
              })}
            </div>
          </ul>
        </div>
      </div>
    );
  };

  // ---------- Desktop focus management refs ----------//
  const level1TriggerRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const level2TriggerRefs = useRef<
    Record<number, (HTMLButtonElement | null)[]>
  >({});
  const level3TriggerRefs = useRef<
    Record<string, (HTMLButtonElement | null)[]>
  >({});
  const level4LinkRefs = useRef<Record<string, HTMLElement[][]>>({});
  const breadcrumbRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const pendingFocus = useRef<
    | null
    | { type: "breadcrumb"; index: number }
    | { type: "level"; level: 2 | 3 | 4; key: string | number }
  >(null);

  /* -------HELPERS------ */
  // Helper to focus the first available element in an array of refs, used for focusing the first link in a menu when opened via keyboard
  const focusFirst = (arr?: (HTMLElement | null)[] | HTMLElement[][]) => {
    if (!arr) return;

    const flat = Array.isArray(arr[0])
      ? (arr as HTMLElement[][]).flat()
      : (arr as HTMLElement[]);

    const el = flat.find((n) => n && typeof n.focus === "function");
    el?.focus();
  };

  // Effect to handle focusing the correct element after state changes that open menus on desktop
  useEffect(() => {
    if (!isDesktop || !pendingFocus.current) return;

    const req = pendingFocus.current;

    const tryFocus = () => {
      if (req.type === "breadcrumb") {
        const el = breadcrumbRefs.current[req.index];

        if (el) {
          el.focus();
          pendingFocus.current = null;
          return true;
        }
        return false;
      }

      if (req.type === "level") {
        if (req.level === 2 && typeof req.key === "number") {
          focusFirst(level2TriggerRefs.current[req.key]);
        }

        if (req.level === 3 && typeof req.key === "string") {
          focusFirst(level3TriggerRefs.current[req.key]);
        }

        if (req.level === 4 && typeof req.key === "string") {
          focusFirst(level4LinkRefs.current[req.key]);
        }

        pendingFocus.current = null;
        return true;
      }

      return false;
    };

    let attempts = 0;

    const rafLoop = () => {
      if (tryFocus() || attempts > 5) return;
      attempts++;
      requestAnimationFrame(rafLoop);
    };

    requestAnimationFrame(rafLoop);
  }, [isDesktop, openLevel1Index, openLevel2Index, openLevel3Index]);

  // Keyboard handler for desktop to open/close menus and focus management
  const onKeyActivate = (
    e: React.KeyboardEvent,
    opts: {
      hasL1Children: boolean;
      onOpen: () => void;
      onLeafNavigate?: () => void;
      onEscape?: () => void;
    },
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (opts.hasL1Children) opts.onOpen();
      else opts.onLeafNavigate?.();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      opts.onEscape?.();
    }
  };

  /*-----LEVEL 1 Menu for desktop-----*/
  const renderLevel1Menu = ({
    item,
    i1,
    isL1Open,
    hasL1Children,
    handleClick,
    pendingFocus,
    level1TriggerRefs,
    setOpenLevel1Index,
    onKeyActivate,
    renderLink,
  }: any) => {
    const handleL1Click = () => {
      handleClick(1, i1, item);
      pendingFocus.current = { type: "breadcrumb", index: 0 };
    };

    const handleL1Escape = () => {
      setOpenLevel1Index(null);
      requestAnimationFrame(() => level1TriggerRefs.current[i1]?.focus());
    };

    if (!hasL1Children) {
      return renderLink(item, isL1Open, 1, "desktop");
    }

    return (
      <button
        ref={(el) => {
          level1TriggerRefs.current[i1] = el;
        }}
        type="button"
        className="cursor-pointer focus-visible:text-charcoal-gray [&:focus-visible_span]:font-medium"
        aria-haspopup="true"
        aria-expanded={isL1Open}
        aria-controls={`mm-l2-${i1}`}
        onClick={handleL1Click}
        onKeyDown={(e) =>
          onKeyActivate(e, {
            hasL1Children: true,
            onOpen: handleL1Click,
            onEscape: handleL1Escape,
          })
        }
      >
        {renderLink(item, isL1Open, 1, "desktop")}
      </button>
    );
  };

  /*-----BREADCRUMB-----*/
  const renderBreadcrumb = ({
    hasDeepNestedChildren,
    openLevel1Index,
    openLevel2Index,
    openLevel3Index,
    level2Items,
    level3Items,
    hasLevel2Children,
    hasLevel3Children,
    breadcrumbRefs,
    setOpenLevel2IndexMap,
    setOpenLevel3IndexMap,
  }: any) => {
    if (!hasDeepNestedChildren) return null;

    return (
      <span data-testid="breadcrumb">
        <span className="flex gap-4 items-center px-2 pt-9 pb-7 text-black font-medium">
          {openLevel2Index === null && (
            <Text
              tag="h2"
              field={{ value: fields?.b1 }}
              className="text-[32px] leading-[40px] m-0 tracking-[0.8px]"
            />
          )}

          {hasLevel2Children && openLevel3Index === null && (
            <>
              <button
                ref={(el) => {
                  breadcrumbRefs.current[0] = el;
                }}
                onClick={() => {
                  if (openLevel1Index !== null) {
                    setOpenLevel2IndexMap((prev: any) => ({
                      ...prev,
                      [openLevel1Index]: null,
                    }));
                    setOpenLevel3IndexMap((prev: any) => {
                      const updated = { ...prev };
                      delete updated[`${openLevel1Index}-${openLevel2Index}`];
                      return updated;
                    });
                  }
                }}
                className="text-ecolab-blue text-[32px] leading-[40px] hover:underline tracking-[0.8px] active:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)] cursor-pointer"
                aria-label={level2Items?.[openLevel2Index]?.t}
              >
                {level2Items?.[openLevel2Index]?.t}
              </button>

              <Icon
                icon={ICONS.CHEVRON_BLUE_RIGHT}
                className="text-ecolab-blue inline-block mx-1"
              />

              <Text
                tag="h3"
                field={{ value: fields?.b2 }}
                className="text-[32px] leading-[40px] m-0 tracking-[0.8px]"
              />
            </>
          )}

          {openLevel2Index !== null && hasLevel3Children && (
            <>
              <button
                ref={(el) => {
                  breadcrumbRefs.current[0] = el;
                }}
                onClick={() => {
                  if (openLevel1Index !== null) {
                    setOpenLevel2IndexMap((prev: any) => ({
                      ...prev,
                      [openLevel1Index]: null,
                    }));
                    setOpenLevel3IndexMap((prev: any) => {
                      const updated = { ...prev };
                      delete updated[`${openLevel1Index}-${openLevel2Index}`];
                      return updated;
                    });
                  }
                }}
                className="text-ecolab-blue text-[32px] leading-[40px] hover:underline cursor-pointer tracking-[0.8px]"
                aria-label={level2Items?.[openLevel2Index]?.t}
              >
                {level2Items?.[openLevel2Index]?.t}
              </button>

              <Icon
                icon={ICONS.CHEVRON_BLUE_RIGHT}
                className="text-ecolab-blue inline-block mx-1"
              />

              <button
                ref={(el) => {
                  breadcrumbRefs.current[1] = el;
                }}
                onClick={() => {
                  const key = `${openLevel1Index}-${openLevel2Index}`;
                  setOpenLevel3IndexMap((prev: any) => {
                    const updated = { ...prev };
                    delete updated[key];
                    return updated;
                  });
                }}
                className="text-ecolab-blue text-[32px] leading-[40px] hover:underline cursor-pointer tracking-[0.8px]"
                aria-label={level3Items?.[openLevel3Index]?.t}
              >
                {level3Items?.[openLevel3Index]?.t}
              </button>

              <Icon
                icon={ICONS.CHEVRON_BLUE_RIGHT}
                className="text-ecolab-blue inline-block mx-1"
              />

              <Text
                tag="h3"
                field={{ value: fields?.b3 }}
                className="text-[32px] leading-[40px] m-0 tracking-[0.8px]"
              />
            </>
          )}
        </span>
      </span>
    );
  };

  /*-----LEVEL 2 Menu for desktop-----*/
  const renderLevel2Menu = (params: any) => {
    const {
      l2Item,
      i1,
      i2,
      handleClick,
      pendingFocus,
      level2TriggerRefs,
      openLevel2Index,
      setOpenLevel2IndexMap,
      level1TriggerRefs,
      onKeyActivate,
      renderLink,
    } = params;

    const hasChildren = !!l2Item?.c?.length;
    const href = l2Item?.l?.h;

    const handleOpen = () => {
      handleClick(2, i2, l2Item);
      pendingFocus.current = { type: "breadcrumb", index: 0 };
    };

    const handleEscape = () => {
      setOpenLevel2IndexMap((prev: any) => ({
        ...prev,
        [i1]: null,
      }));

      requestAnimationFrame(() => level1TriggerRefs.current[i1]?.focus());
    };

    const handleLeafClick = () => {
      resetMenuState();
    };

    return (
      <li data-testid={`level2-item-${i2}`} key={`level2-item-${i2}`}>
        {hasChildren ? (
          renderLink(l2Item, openLevel2Index === i2, 2, "desktop", false, {
            ref: (el: HTMLDivElement | null) => {
              if (el) level2TriggerRefs.current[i1][i2] = el;
            },
            onClick: handleOpen,
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) =>
              onKeyActivate(e, {
                hasL1Children: true,
                onOpen: handleOpen,
                onEscape: handleEscape,
              }),
            role: "button",
            tabIndex: 0,
            ariaHaspopup: true,
            ariaExpanded: openLevel2Index === i2,
            ariaControls: `mm-l3-${i1}-${i2}`,
          })
        ) : (
          <span
            onClick={handleLeafClick}
            onKeyDown={(e) =>
              onKeyActivate(e, {
                hasL1Children: false,
                onOpen: () => {},
                onLeafNavigate: () => {
                  handleLeafClick();
                  if (href) {
                    globalThis.location.href = href;
                  }
                },
              })
            }
          >
            {renderLink(l2Item, false, 2, "desktop")}
          </span>
        )}
      </li>
    );
  };

  /*-----LEVEL 3 Menu for desktop-----*/
  const renderLevel3Menu = (params: any) => {
    const {
      l3Item,
      i1,
      i3,
      handleClick,
      pendingFocus,
      level3TriggerRefs,
      openLevel2Index,
      openLevel3Index,
      setOpenLevel3IndexMap,
      level2TriggerRefs,
      onKeyActivate,
      renderLink,
    } = params;

    const hasChildren = !!l3Item?.c?.length;
    const href = l3Item?.l?.h;

    const key = `${i1}-${openLevel2Index}`;

    if (!level3TriggerRefs.current[key]) {
      level3TriggerRefs.current[key] = [];
    }

    const handleOpen = () => {
      handleClick(3, i3, l3Item);
      pendingFocus.current = { type: "breadcrumb", index: 1 };
    };

    const handleEscape = () => {
      setOpenLevel3IndexMap((prev: any) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });

      requestAnimationFrame(() =>
        level2TriggerRefs.current[i1]?.[openLevel2Index]?.focus(),
      );
    };

    const handleLeafClick = () => {
      resetMenuState();
    };

    return (
      <li
        key={`d-l3-${i3}-${l3Item.t || i3}`}
        data-testid={`level3-item-${i3}`}
        className="cursor-pointer"
      >
        {hasChildren ? (
          renderLink(l3Item, openLevel3Index === i3, 3, "desktop", false, {
            ref: (el: HTMLDivElement | null) => {
              if (el) level3TriggerRefs.current[key][i3] = el;
            },
            onClick: handleOpen,
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) =>
              onKeyActivate(e, {
                hasL1Children: true,
                onOpen: handleOpen,
                onEscape: handleEscape,
              }),
            role: "button",
            tabIndex: 0,
            ariaHaspopup: true,
            ariaExpanded: openLevel3Index === i3,
            ariaControls: `mm-l4-${i1}-${openLevel2Index}-${i3}`,
          })
        ) : (
          <span
            onClick={handleLeafClick}
            onKeyDown={(e) =>
              onKeyActivate(e, {
                hasL1Children: false,
                onOpen: () => {},
                onLeafNavigate: () => {
                  handleLeafClick();
                  if (href) {
                    globalThis.location.href = href;
                  }
                },
              })
            }
          >
            {renderLink(l3Item, false, 3, "desktop")}
          </span>
        )}
      </li>
    );
  };

  /*-----Helpers for LEVEL 4 Items for desktop-----*/
  const getLevel4Key = (i1: number, l2: number, l3: number) =>
    `${i1}-${l2}-${l3}`;

  const ensureLevel4Refs = (
    level4LinkRefs: any,
    key: string,
    columnIndex?: number,
  ) => {
    if (!level4LinkRefs.current[key]) {
      level4LinkRefs.current[key] = [];
    }

    if (
      columnIndex !== undefined &&
      !level4LinkRefs.current[key][columnIndex]
    ) {
      level4LinkRefs.current[key][columnIndex] = [];
    }
  };

  const assignLevel4Ref = (
    level4LinkRefs: any,
    key: string,
    colIndex: number,
    linkIndex: number,
    el: HTMLElement | null,
  ) => {
    if (!el) return;
    level4LinkRefs.current[key][colIndex][linkIndex] = el;
  };

  const handleLevel4Escape = (
    e: React.KeyboardEvent,
    i1: number,
    openLevel2Index: number,
    openLevel3Index: number,
    level3TriggerRefs: any,
    setOpenLevel3IndexMap: any,
  ) => {
    if (e.key !== "Escape") return;

    e.preventDefault();

    const key = `${i1}-${openLevel2Index}`;

    setOpenLevel3IndexMap((prev: any) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });

    requestAnimationFrame(() =>
      level3TriggerRefs.current[key]?.[openLevel3Index]?.focus(),
    );
  };

  type RenderLevel4LinksProps = {
    col: any;
    colIndex: number;
    l4Key: string;
    i1: number;
    openLevel2Index: number;
    openLevel3Index: number;
    level4LinkRefs: any;
    level3TriggerRefs: any;
    setOpenLevel3IndexMap: any;
    resetMenuState: () => void;
    selectedIndustry?: string;
    selectedMarket?: string;
    selectedApplication?: string;
  };

  const renderLevel4Links = ({
    col,
    colIndex,
    l4Key,
    i1,
    openLevel2Index,
    openLevel3Index,
    level4LinkRefs,
    level3TriggerRefs,
    setOpenLevel3IndexMap,
    resetMenuState,
    selectedIndustry,
    selectedMarket,
    selectedApplication,
  }: RenderLevel4LinksProps & {
    selectedIndustry?: string;
    selectedMarket?: string;
  }) => {
    ensureLevel4Refs(level4LinkRefs, l4Key, colIndex);

    return (col.c || []).map((link: any, linkIndex: number) => {
      let href = link?.l?.h;
      if (href && (selectedIndustry || selectedMarket)) {
        const url = new URL(href, globalThis.location.origin);
        let industry = "";
        let markets = "";
        let application = "";
        let solution = link?.l?.s || "";
        if (selectedIndustry) {
          industry = encodeHashValue(selectedIndustry);
        }
        if (selectedMarket) {
          markets = encodeHashValue(selectedMarket);
        }
        if (selectedApplication) {
          application = encodeHashValue(selectedApplication);
        }
        if (solution) {
          solution = encodeHashValue(solution);
        }
        const hashPart = buildHashUrlForMegaMenu(
          industry,
          markets,
          application,
          solution,
        );

        url.hash = hashPart.startsWith("#") ? hashPart.substring(1) : hashPart;
        href = url.toString();
      }

      const mappedLink: LinkField = {
        value: {
          href: href,
          text: link?.l?.t || link?.t,
          linktype:
            link?.l?.h?.startsWith("http") && isExternalURL(link?.l?.h)
              ? "external"
              : "internal",
        },
      };

      return (
        <li
          key={`d-l4-link-${linkIndex}-${link?.l?.t || linkIndex}`}
          ref={(el) =>
            assignLevel4Ref(level4LinkRefs, l4Key, colIndex, linkIndex, el)
          }
          onClick={resetMenuState}
        >
          <Button
            className="text-ecolab-blue font-normal flex items-center gap-2 !py-[6px] !px-0 hover:underline tracking-[0.4px] leading-[19px] !min-h-0 !min-w-0 focus-visible:rounded-none !justify-end !flex-nowrap"
            iconPosition="right"
            jssIconField={{ value: ICONS.CHEVRON_BLUE_RIGHT }}
            field={mappedLink}
            tabIndex={0}
            onKeyDown={(e) =>
              handleLevel4Escape(
                e,
                i1,
                openLevel2Index,
                openLevel3Index,
                level3TriggerRefs,
                setOpenLevel3IndexMap,
              )
            }
          />
        </li>
      );
    });
  };

  /*-----LEVEL 4 Items for desktop-----*/
  const renderLevel4Menu = ({
    i1,
    level4Items,
    openLevel2Index,
    openLevel3Index,
    level4LinkRefs,
    level3TriggerRefs,
    setOpenLevel3IndexMap,
    resetMenuState,
  }: any) => {
    const l4Key = getLevel4Key(i1, openLevel2Index, openLevel3Index);
    const level2Items = i1 === null ? [] : level1Items[i1]?.c || [];
    const selectedIndustry =
      openLevel2Index === null ? undefined : level2Items[openLevel2Index]?.i;
    const selectedMarket =
      openLevel3Index === null ? undefined : level3Items[openLevel3Index]?.m;

    ensureLevel4Refs(level4LinkRefs, l4Key);

    return (
      <ul
        id={`mm-l4-${i1}-${openLevel2Index}-${openLevel3Index}`}
        className="grid grid-cols-4 pt-9 pb-[30px] bg-white -mx-8 px-8 gap-8"
      >
        {level4Items.map((col: any, colIndex: number) => {
          const selectedApplication = col?.t;
          return (
            <li
              key={`d-l4-${colIndex}-${col.t || colIndex}`}
              data-testid={`level4-item-${colIndex}`}
            >
              <Text
                tag="p"
                field={{ value: col?.t }}
                className="font-medium mb-[10px] text-black"
              />

              <ul>
                {renderLevel4Links({
                  col,
                  colIndex,
                  l4Key,
                  i1,
                  openLevel2Index,
                  openLevel3Index,
                  level4LinkRefs,
                  level3TriggerRefs,
                  setOpenLevel3IndexMap,
                  resetMenuState,
                  selectedIndustry,
                  selectedMarket,
                  selectedApplication,
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    );
  };

  /*-----MAIN DESKTOP MENU-----*/
  const renderDesktopMenu = () => {
    return (
      <div
        className="megamenu-desktop-component relative"
        ref={menuRef}
        data-role="megamenu-component"
      >
        <div
          className={`${styles.headerShadow} relative z-20`}
          data-testid="mega-menu-desktop"
        >
          <div className="relative z-10 flex items-center px-8 pt-[19px] h-[95px] mx-auto max-w-(--sitewide-wide-container-max-width)">
            <LogoComponent
              className="min-h-[68px] pl-[18px] py-[10px] pr-3 [&_img]:max-h-[42px] [&>div]:mb-0 [&_img]:rounded-none [&_img]:object-contain [&_img]:max-w-[146px] [&_img]:max-w-[146px] [&_img]:max-h-[42px] [&_img]:object-contain"
              selectedPlaceholder={selectedPlaceholder}
              logoOpacity={logoOpacity}
              rendering={rendering}
              page={page}
              componentMap={componentMap}
            />

            <ul className="flex px-4">
              {level1Items.map((item, i1) => {
                const isL1Open = openLevel1Index === i1;
                const l2 = item?.c || [];
                const hasL1Children = l2.length > 0;

                if (!level2TriggerRefs.current[i1]) {
                  level2TriggerRefs.current[i1] = [];
                }

                return (
                  <li
                    key={`d-l1-${i1}-${item.t || i1}`}
                    data-testid={`level1-item-${i1}`}
                    data-role={`mega-menu-level1-item-${i1}`}
                    className={`h-[76px] ${isL1Open ? "border-b-2 border-b-ecolab-blue" : ""}`}
                  >
                    {renderLevel1Menu({
                      item,
                      i1,
                      isL1Open,
                      hasL1Children,
                      handleClick,
                      pendingFocus,
                      level1TriggerRefs,
                      setOpenLevel1Index,
                      onKeyActivate,
                      renderLink,
                    })}

                    {isL1Open && hasL1Children && (
                      <span
                        id={`mm-l2-${i1}`}
                        className="bg-light-gray absolute top-[95px] w-full z-10 px-8 shadow-[0_8px_16px_0_rgba(0,0,0,0.15)] left-0"
                      >
                        <span className="max-w-(--sitewide-wide-container-max-width) mx-auto ">
                          {renderBreadcrumb({
                            hasDeepNestedChildren,
                            openLevel1Index,
                            openLevel2Index,
                            openLevel3Index,
                            level1Items,
                            level2Items,
                            level3Items,
                            hasLevel2Children,
                            hasLevel3Children,
                            breadcrumbRefs,
                            setOpenLevel2IndexMap,
                            setOpenLevel3IndexMap,
                          })}

                          {openLevel2Index === null && (
                            <ul
                              className={`grid grid-cols-4 ${
                                openLevel1Index === 0 ? "pb-7" : "p-2"
                              }`}
                            >
                              {l2.map((l2Item, i2) =>
                                renderLevel2Menu({
                                  l2Item,
                                  i1,
                                  i2,
                                  handleClick,
                                  pendingFocus,
                                  level2TriggerRefs,
                                  openLevel2Index,
                                  setOpenLevel2IndexMap,
                                  level1TriggerRefs,
                                  onKeyActivate,
                                  renderLink,
                                }),
                              )}
                            </ul>
                          )}

                          {openLevel2Index !== null &&
                            openLevel3Index === null && (
                              <ul
                                id={`mm-l3-${i1}-${openLevel2Index}`}
                                className="grid grid-cols-4 pb-7"
                              >
                                {level3Items.map((l3Item, i3) =>
                                  renderLevel3Menu({
                                    l3Item,
                                    i1,
                                    i3,
                                    handleClick,
                                    pendingFocus,
                                    level3TriggerRefs,
                                    openLevel2Index,
                                    openLevel3Index,
                                    setOpenLevel3IndexMap,
                                    level2TriggerRefs,
                                    onKeyActivate,
                                    renderLink,
                                  }),
                                )}
                              </ul>
                            )}

                          {openLevel3Index !== null &&
                            hasDeepNestedChildren &&
                            renderLevel4Menu({
                              i1,
                              level4Items,
                              openLevel2Index,
                              openLevel3Index,
                              level4LinkRefs,
                              level3TriggerRefs,
                              setOpenLevel3IndexMap,
                              resetMenuState,
                            })}
                        </span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="ml-auto">
              <SearchIconComponent
                searchPlaceholder={searchPlaceholder}
                rendering={rendering}
                page={page}
                componentMap={componentMap}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  //main render function to decide which menu version to render based on device type
  const renderMegaMenu = () => {
    if (isMobile || isTablet) {
      return renderResponsiveMenu();
    } else if (isDesktop) {
      return renderDesktopMenu();
    } else {
      return null;
    }
  };

  //function to handle clicks outside the menu to close any open dropdowns and reset states for mobile and tablet
  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    const menuNode = menuRef.current;
    const clickedInsideMenu = menuNode?.contains(target);
    const isDesktopView = window.innerWidth >= 1280;
    const pageScrollbarClick =
      isDesktopView &&
      window.innerWidth - document.documentElement.clientWidth > 0 &&
      event.clientX >= document.documentElement.clientWidth;

    if (!clickedInsideMenu && !pageScrollbarClick && isDesktopView)
      setOpenLevel1Index(null);
    const insideTablet = tabletRefs.current.some((ref) =>
      ref?.contains(target),
    );
    const insideMobile = mobileRef.current?.contains(target);
    setLevel1ClickedOutside(!(insideTablet || insideMobile));
  }, []);

  const handleEscKey = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setOpenLevel1Index(null);

      //Manually blur the currently focused element to prevent it from being visually focused
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  }, []);

  const handleWindowResize = useCallback(() => {
    if (typeof globalThis === "undefined") return;

    const currentWidth = window.innerWidth;
    const previousWidth = previousViewportWidthRef.current;

    if (previousWidth !== null && currentWidth !== previousWidth) {
      resetMenuState();
    }

    previousViewportWidthRef.current = currentWidth;
  }, [resetMenuState]);

  //effect to reset menu state on actual viewport width changes for mobile and tablet
  useEffect(() => {
    if (typeof globalThis !== "undefined") {
      previousViewportWidthRef.current = window.innerWidth;
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);
    window.addEventListener("resize", handleWindowResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [handleClickOutside, handleEscKey, handleWindowResize]);

  // effect to load the logo in the menu once placeholder keys are available, and set opacity for fade-in effect
  useEffect(() => {
    if (placeholderKeys.length === 0) return;
    const companyMetaValues =
      typeof document === "undefined"
        ? []
        : Array.from(
            document.querySelectorAll('meta[name="ecolab_taxonomy_companies"]'),
          )
            .map((meta) => meta.getAttribute("content")?.trim())
            .filter(Boolean);

    const shouldUseNalcoLogo = companyMetaValues.some(
      (value) => value?.toLowerCase() === "nalco water",
    );
    const newSelectedPlaceholder = shouldUseNalcoLogo
      ? nalcoPlaceholder || headerPlaceholder
      : headerPlaceholder;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedPlaceholder(newSelectedPlaceholder || "mainnavlogo-7");

    setLogoOpacity("opacity-100 transition-opacity duration-300");
  }, [
    placeholderKeys,
    headerPlaceholder,
    nalcoPlaceholder,
    globalThis?.window?.location?.pathname,
    page,
  ]);

  if (!fields) {
    return <NoDataFallback componentName="Mega Menu" />;
  }

  // `renderMegaMenu` accesses refs internally to compose its layout; reading
  // those refs during render is intentional here (existing pattern from the
  // JSS 22.5 era), so the React Compiler warnings are safe to suppress.
  // eslint-disable-next-line react-hooks/refs
  return <div className="min-h-[95px]">{renderMegaMenu?.()}</div>;
};
